import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Palette, Layers, Target, Zap, Send, Sparkles, PenTool, RefreshCw, Eye, Award, TrendingUp, Users, Lightbulb, FileCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../hooks/use-toast';
import { useTranslation } from 'react-i18next';

const LogoDesign = () => {
  const { isDark } = useTheme();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast({
      title: t('logoDesign.submitSuccess'),
      description: t('logoDesign.submitSuccessDesc'),
    });
    setFormData({ name: '', email: '', phone: '', company: '', industry: '', message: '' });
    setIsSubmitting(false);
  };

  const mainServices = [
    {
      icon: PenTool,
      title: t('logoDesign.customLogo'),
      description: t('logoDesign.customLogoDesc'),
      features: [t('logoDesign.feature1'), t('logoDesign.feature2'), t('logoDesign.feature3'), t('logoDesign.feature4')],
      color: isDark ? '#ff00ff' : '#ff1493'
    },
    {
      icon: RefreshCw,
      title: t('logoDesign.logoRefresh'),
      description: t('logoDesign.logoRefreshDesc'),
      features: [t('logoDesign.feature5'), t('logoDesign.feature6'), t('logoDesign.feature7'), t('logoDesign.feature8')],
      color: isDark ? '#00ffff' : '#00bfff'
    },
    {
      icon: Layers,
      title: t('logoDesign.brandIdentity'),
      description: t('logoDesign.brandIdentityDesc'),
      features: [t('logoDesign.feature9'), t('logoDesign.feature10'), t('logoDesign.feature11'), t('logoDesign.feature12')],
      color: isDark ? '#ffff00' : '#f59e0b'
    },
    {
      icon: Zap,
      title: t('logoDesign.signageOptimized'),
      description: t('logoDesign.signageOptimizedDesc'),
      features: [t('logoDesign.feature13'), t('logoDesign.feature14'), t('logoDesign.feature15'), t('logoDesign.feature16')],
      color: isDark ? '#00ff88' : '#10b981'
    }
  ];

  const howWeHelp = [
    {
      icon: Eye,
      title: t('logoDesign.standOut'),
      description: t('logoDesign.standOutDesc')
    },
    {
      icon: Users,
      title: t('logoDesign.connect'),
      description: t('logoDesign.connectDesc')
    },
    {
      icon: TrendingUp,
      title: t('logoDesign.growth'),
      description: t('logoDesign.growthDesc')
    },
    {
      icon: Award,
      title: t('logoDesign.recognition'),
      description: t('logoDesign.recognitionDesc')
    }
  ];

  const processSteps = [
    { 
      step: '01', 
      title: t('logoDesign.discovery'), 
      description: t('logoDesign.discoveryDesc'),
      details: t('logoDesign.discoveryDetails', { returnObjects: true })
    },
    { 
      step: '02', 
      title: t('logoDesign.concept'), 
      description: t('logoDesign.conceptDesc'),
      details: t('logoDesign.conceptDetails', { returnObjects: true })
    },
    { 
      step: '03', 
      title: t('logoDesign.refinement'), 
      description: t('logoDesign.refinementDesc'),
      details: t('logoDesign.refinementDetails', { returnObjects: true })
    },
    { 
      step: '04', 
      title: t('logoDesign.delivery'), 
      description: t('logoDesign.deliveryDesc'),
      details: t('logoDesign.deliveryDetails', { returnObjects: true })
    }
  ];

  const deliverables = t('logoDesign.deliverablesList', { returnObjects: true });

  const whyChooseUs = [
    {
      title: t('logoDesign.whySignageExpertise'),
      description: t('logoDesign.whySignageExpertiseDesc')
    },
    {
      title: t('logoDesign.whyStrategicApproach'),
      description: t('logoDesign.whyStrategicApproachDesc')
    },
    {
      title: t('logoDesign.whyUnlimitedRevisions'),
      description: t('logoDesign.whyUnlimitedRevisionsDesc')
    },
    {
      title: t('logoDesign.whyFullOwnership'),
      description: t('logoDesign.whyFullOwnershipDesc')
    }
  ];

  return (
    <div 
      className="min-h-screen pt-24 md:pt-28 pb-16 transition-colors duration-300"
      style={{ backgroundColor: isDark ? '#000000' : '#ffffff' }}
    >
      <div className="max-w-7xl mx-auto px-4">
        {/* Back Button */}
        <Link 
          to="/"
          className="inline-flex items-center gap-2 mb-8 font-medium transition-all duration-300 hover:scale-105"
          style={{
            color: isDark ? '#00ffff' : '#0f172a',
            textShadow: isDark ? '0 0 10px rgba(0, 255, 255, 0.5)' : 'none'
          }}
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-5 h-5" />
          {t('common.backToHome')}
        </Link>

        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border"
              style={{
                backgroundColor: isDark ? 'rgba(20, 20, 20, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                borderColor: isDark ? '#ff00ff' : '#ff1493',
                borderWidth: '2px'
              }}
            >
              <Sparkles className="w-4 h-4" style={{ color: isDark ? '#ff00ff' : '#ff1493' }} />
              <span style={{ color: isDark ? '#00ffff' : '#ff00ff' }}>{t('logoDesign.badge')}</span>
            </div>
          </div>
          
          <h1 
            className={`text-4xl md:text-6xl font-bold mb-6 ${isDark ? 'gradient-text-dark' : 'gradient-text-light'}`}
            style={{
              filter: isDark 
                ? 'drop-shadow(0 0 20px rgba(255, 0, 255, 0.5))'
                : 'drop-shadow(0 0 15px rgba(255, 20, 147, 0.4))'
            }}
          >
            {t('logoDesign.title')}
          </h1>
          <p 
            className="text-lg md:text-xl max-w-4xl mx-auto mb-4"
            style={{ color: isDark ? '#e0e0e0' : '#334155' }}
          >
            {t('logoDesign.subtitle1')}
          </p>
          <p 
            className="text-base md:text-lg max-w-3xl mx-auto"
            style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
          >
            {t('logoDesign.subtitle2')}
          </p>
        </div>

        {/* How We Help Your Business */}
        <div className="mb-20">
          <h2 
            className="text-3xl md:text-4xl font-bold text-center mb-4"
            style={{
              color: isDark ? '#ff00ff' : '#ff1493',
              textShadow: isDark ? '0 0 20px rgba(255, 0, 255, 0.6)' : 'none'
            }}
          >
            {t('logoDesign.howWeHelp')}
          </h2>
          <p 
            className="text-center max-w-2xl mx-auto mb-12"
            style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
          >
            {t('logoDesign.howWeHelpSubtitle')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howWeHelp.map((item, index) => (
              <div 
                key={index}
                className="text-center p-6 rounded-xl transition-all duration-300 hover:scale-105"
                style={{
                  backgroundColor: isDark ? 'rgba(20, 20, 20, 0.6)' : 'rgba(248, 250, 252, 1)',
                  border: `1px solid ${isDark ? 'rgba(255, 0, 255, 0.3)' : 'rgba(255, 20, 147, 0.2)'}`
                }}
              >
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{
                    background: isDark 
                      ? 'linear-gradient(135deg, rgba(255, 0, 255, 0.2) 0%, rgba(0, 255, 255, 0.2) 100%)'
                      : 'linear-gradient(135deg, rgba(255, 20, 147, 0.15) 0%, rgba(0, 191, 255, 0.15) 100%)',
                    boxShadow: isDark ? '0 0 25px rgba(255, 0, 255, 0.3)' : 'none'
                  }}
                >
                  <item.icon className="w-7 h-7" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }} />
                </div>
                <h3 
                  className="text-lg font-bold mb-2"
                  style={{ color: isDark ? '#ffffff' : '#0f172a' }}
                >
                  {item.title}
                </h3>
                <p 
                  className="text-sm"
                  style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
                >
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Main Services */}
        <div className="mb-20">
          <h2 
            className="text-3xl md:text-4xl font-bold text-center mb-4"
            style={{
              color: isDark ? '#00ffff' : '#0ea5e9',
              textShadow: isDark ? '0 0 20px rgba(0, 255, 255, 0.6)' : 'none'
            }}
          >
            {t('logoDesign.services')}
          </h2>
          <p 
            className="text-center max-w-2xl mx-auto mb-12"
            style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
          >
            {t('logoDesign.servicesSubtitle')}
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {mainServices.map((service, index) => (
              <div
                key={index}
                className="p-8 rounded-2xl border transition-all duration-300 hover:scale-[1.01]"
                style={{
                  backgroundColor: isDark ? 'rgba(20, 20, 20, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                  borderColor: service.color,
                  borderWidth: '2px',
                  boxShadow: isDark ? `0 0 40px ${service.color}22` : `0 0 30px ${service.color}15`
                }}
              >
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center mb-5"
                  style={{
                    background: `${service.color}22`,
                    boxShadow: isDark ? `0 0 30px ${service.color}44` : 'none'
                  }}
                >
                  <service.icon className="w-8 h-8" style={{ color: service.color }} />
                </div>
                <h3 
                  className="text-2xl font-bold mb-3"
                  style={{ 
                    color: service.color,
                    textShadow: isDark ? `0 0 15px ${service.color}88` : 'none'
                  }}
                >
                  {service.title}
                </h3>
                <p 
                  className="mb-5"
                  style={{ color: isDark ? '#c0c0c0' : '#64748b' }}
                >
                  {service.description}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {service.features.map((feature, fIndex) => (
                    <div 
                      key={fIndex}
                      className="flex items-center gap-2 text-sm"
                      style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
                    >
                      <Check className="w-4 h-4 flex-shrink-0" style={{ color: isDark ? '#00ff88' : '#10b981' }} />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Process Section */}
        <div className="mb-20">
          <h2 
            className="text-3xl md:text-4xl font-bold text-center mb-4"
            style={{
              color: isDark ? '#ffff00' : '#f59e0b',
              textShadow: isDark ? '0 0 20px rgba(255, 255, 0, 0.6)' : 'none'
            }}
          >
            {t('logoDesign.process')}
          </h2>
          <p 
            className="text-center max-w-2xl mx-auto mb-12"
            style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
          >
            {t('logoDesign.processSubtitle')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((item, index) => (
              <div 
                key={index}
                className="p-6 rounded-xl relative"
                style={{
                  backgroundColor: isDark ? 'rgba(10, 10, 10, 0.8)' : 'rgba(248, 250, 252, 1)',
                  border: `1px solid ${isDark ? 'rgba(255, 255, 0, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`
                }}
              >
                <div 
                  className="text-5xl font-bold mb-4 opacity-30"
                  style={{ color: isDark ? '#ffff00' : '#f59e0b' }}
                >
                  {item.step}
                </div>
                <h3 
                  className="text-xl font-bold mb-2"
                  style={{ color: isDark ? '#ffffff' : '#0f172a' }}
                >
                  {item.title}
                </h3>
                <p 
                  className="text-sm mb-4"
                  style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
                >
                  {item.description}
                </p>
                <div className="space-y-1">
                  {item.details.map((detail, dIndex) => (
                    <div 
                      key={dIndex}
                      className="flex items-center gap-2 text-xs"
                      style={{ color: isDark ? '#808080' : '#94a3b8' }}
                    >
                      <div className="w-1 h-1 rounded-full" style={{ backgroundColor: isDark ? '#ffff00' : '#f59e0b' }} />
                      {detail}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* What You Receive */}
        <div 
          className="p-8 md:p-12 rounded-2xl mb-20"
          style={{
            backgroundColor: isDark ? 'rgba(10, 10, 10, 0.9)' : 'rgba(248, 250, 252, 1)',
            border: `2px solid ${isDark ? '#00ffff' : '#00bfff'}`,
            boxShadow: isDark ? '0 0 50px rgba(0, 255, 255, 0.15)' : 'none'
          }}
        >
          <div className="flex items-center justify-center gap-3 mb-8">
            <FileCheck className="w-8 h-8" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }} />
            <h2 
              className="text-2xl md:text-3xl font-bold"
              style={{
                color: isDark ? '#00ffff' : '#0ea5e9',
                textShadow: isDark ? '0 0 15px rgba(0, 255, 255, 0.6)' : 'none'
              }}
            >
              {t('logoDesign.deliverables')}
            </h2>
          </div>
          <p 
            className="text-center max-w-2xl mx-auto mb-8"
            style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
          >
            {t('logoDesign.deliverablesSubtitle')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {deliverables.map((item, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 p-3 rounded-lg"
                style={{
                  backgroundColor: isDark ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.8)'
                }}
              >
                <Check className="w-5 h-5 flex-shrink-0" style={{ color: isDark ? '#00ff88' : '#10b981' }} />
                <span style={{ color: isDark ? '#e0e0e0' : '#334155' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="mb-20">
          <h2 
            className="text-3xl md:text-4xl font-bold text-center mb-12"
            style={{
              color: isDark ? '#ff00ff' : '#ff1493',
              textShadow: isDark ? '0 0 20px rgba(255, 0, 255, 0.6)' : 'none'
            }}
          >
            {t('logoDesign.whyChoose')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {whyChooseUs.map((item, index) => (
              <div 
                key={index}
                className="p-6 rounded-xl flex gap-4"
                style={{
                  backgroundColor: isDark ? 'rgba(20, 20, 20, 0.6)' : 'rgba(248, 250, 252, 1)',
                  border: `1px solid ${isDark ? 'rgba(255, 0, 255, 0.2)' : 'rgba(255, 20, 147, 0.15)'}`
                }}
              >
                <Lightbulb className="w-8 h-8 flex-shrink-0 mt-1" style={{ color: isDark ? '#ffff00' : '#f59e0b' }} />
                <div>
                  <h3 
                    className="text-lg font-bold mb-2"
                    style={{ color: isDark ? '#ffffff' : '#0f172a' }}
                  >
                    {item.title}
                  </h3>
                  <p style={{ color: isDark ? '#a0a0a0' : '#64748b' }}>
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Form */}
        <div 
          className="max-w-2xl mx-auto p-8 rounded-2xl"
          style={{
            backgroundColor: isDark ? 'rgba(20, 20, 20, 0.8)' : 'rgba(255, 255, 255, 1)',
            border: `3px solid ${isDark ? '#ff00ff' : '#ff1493'}`,
            boxShadow: isDark ? '0 0 50px rgba(255, 0, 255, 0.3)' : '0 0 40px rgba(255, 20, 147, 0.2)'
          }}
        >
          <h2 
            className="text-2xl font-bold mb-2 text-center"
            style={{
              color: isDark ? '#ff00ff' : '#ff1493',
              textShadow: isDark ? '0 0 15px rgba(255, 0, 255, 0.6)' : 'none'
            }}
          >
            {t('logoDesign.startProject')}
          </h2>
          <p 
            className="mb-6 text-center"
            style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
          >
            {t('logoDesign.startProjectSubtitle')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#e0e0e0' : '#334155' }}>
                  {t('quoteForm.name')} *
                </label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder={t('quoteForm.namePlaceholder')}
                  style={{
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                    borderColor: isDark ? '#ff00ff' : '#e2e8f0',
                    color: isDark ? '#ffffff' : '#0f172a'
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#e0e0e0' : '#334155' }}>
                  {t('quoteForm.email')} *
                </label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder={t('quoteForm.emailPlaceholder')}
                  style={{
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                    borderColor: isDark ? '#ff00ff' : '#e2e8f0',
                    color: isDark ? '#ffffff' : '#0f172a'
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#e0e0e0' : '#334155' }}>
                  {t('quoteForm.phone')}
                </label>
                <Input
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder={t('quoteForm.phonePlaceholder')}
                  style={{
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                    borderColor: isDark ? '#ff00ff' : '#e2e8f0',
                    color: isDark ? '#ffffff' : '#0f172a'
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#e0e0e0' : '#334155' }}>
                  {t('logoDesign.businessName')} *
                </label>
                <Input
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  required
                  placeholder={t('logoDesign.businessName')}
                  style={{
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                    borderColor: isDark ? '#ff00ff' : '#e2e8f0',
                    color: isDark ? '#ffffff' : '#0f172a'
                  }}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#e0e0e0' : '#334155' }}>
                {t('logoDesign.industry')}
              </label>
              <Input
                name="industry"
                value={formData.industry}
                onChange={handleInputChange}
                placeholder={t('logoDesign.industryPlaceholder')}
                style={{
                  backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                  borderColor: isDark ? '#ff00ff' : '#e2e8f0',
                  color: isDark ? '#ffffff' : '#0f172a'
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#e0e0e0' : '#334155' }}>
                {t('logoDesign.projectDetails')} *
              </label>
              <Textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows={5}
                placeholder={t('logoDesign.projectPlaceholder')}
                className="resize-none"
                style={{
                  backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                  borderColor: isDark ? '#ff00ff' : '#e2e8f0',
                  color: isDark ? '#ffffff' : '#0f172a'
                }}
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-6 text-lg font-bold transition-all duration-300 hover:scale-105"
              style={{
                background: isDark 
                  ? 'linear-gradient(135deg, #ff00ff 0%, #ff0080 100%)'
                  : 'linear-gradient(135deg, #ff1493 0%, #ff0080 100%)',
                color: '#ffffff',
                boxShadow: isDark 
                  ? '0 10px 40px rgba(255, 0, 255, 0.5)' 
                  : '0 10px 30px rgba(255, 20, 147, 0.4)'
              }}
            >
              {isSubmitting ? t('common.sending') : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  {t('logoDesign.getCustomQuote')}
                </>
              )}
            </Button>

            <p 
              className="text-center text-sm"
              style={{ color: isDark ? '#707070' : '#94a3b8' }}
            >
              {t('logoDesign.freeConsultation')}
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LogoDesign;
